import "./textarea-next.css";
import {ReactNode, useEffect, useRef, useState} from "react";
import {LeftCircleFilled, LeftOutlined, RightCircleFilled, RightOutlined} from "@ant-design/icons";
import {WarningIcon} from "../../../assets/icons/WarningIcon";

interface TextAreaNextProps {
    name?: string;
    value: string;
    setValue: (newVal: string) => void;
    helpers?: {
        title: string;
        options: string[];
    };
    guide?: string;
    note?: string | ReactNode | ReactNode[];
    label?: string;
    direction?: 'ltr' | 'rtl';
    errors?: string[];
}

const TextAreaNext = ({ name = '', direction = 'ltr', value, setValue, helpers, guide, note, label = '', errors }: TextAreaNextProps) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const helpersOptionsRef = useRef<HTMLDivElement | null>(null);
    const [areHelpersWithScroll, setAreHelpersWithScroll] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const addHelper = (helper: string) => () => {
        const start = textareaRef.current?.selectionStart || 0;
        const end = textareaRef.current?.selectionEnd || 0;
        const slicedValue = start < end ? `${value.slice(0, start)}${value.slice(end)}` : value;
        const firstPart = slicedValue.substring(0, start);
        const secondPart = slicedValue.substring(start);
        const newValue = `${firstPart} {{${helper}}} ${secondPart}`;
        setValue(newValue);
        setTimeout(() => {
            textareaRef.current?.setSelectionRange(start + helper.length + 6, start + helper.length + 6);
            textareaRef.current?.focus();
        });
    }

    useEffect(() => {
        const width = helpersOptionsRef.current?.scrollWidth;
        setAreHelpersWithScroll(width ? width > 320 : false);
    }, [helpers]);

    const scrollHelpers = (direction: 'right' | 'left') => {
        if(!helpersOptionsRef.current || helpersOptionsRef.current.scrollWidth <= 320) {
            return;
        }

        const firstChild = helpersOptionsRef.current.firstElementChild as HTMLElement | null;
        if (!firstChild) {
            console.error("No child elements found in the container");
            return;
        }
        const childWidth = firstChild.offsetWidth;

        const newLeft = direction === 'right' ? helpersOptionsRef.current.scrollLeft + childWidth : helpersOptionsRef.current.scrollLeft - childWidth;

        helpersOptionsRef.current.scrollTo({
            left: newLeft,
            behavior: "smooth",
        });

        setCanScrollLeft(helpersOptionsRef.current.scrollLeft > 0);
        setCanScrollRight(helpersOptionsRef.current.scrollLeft + helpersOptionsRef.current.clientWidth < helpersOptionsRef.current.scrollWidth);
    }

    return (
        <div className="field-container">
            <div className={`textarea-container ${errors?.length ? 'with-errors' : ''}`}>
                <textarea ref={textareaRef} placeholder={label || "Your text message"} value={value} onChange={(event) => setValue(event.target.value)} className="content-input" style={{ direction }} />
                {helpers ? <div className="helpers">
                        <div className="helper-description">{helpers.title}:</div>
                        {areHelpersWithScroll ? <div className={`right-navigator ${canScrollRight ? 'scrollable' : ''}`} onClick={() => scrollHelpers('right')}><RightCircleFilled /></div> : null}
                        {areHelpersWithScroll ? <div className={`left-navigator ${canScrollLeft ? 'scrollable' : ''}`} onClick={() => scrollHelpers('left')}><LeftCircleFilled /></div> : null}
                        <div ref={helpersOptionsRef} className={`helpers-options ${areHelpersWithScroll ? 'with-scroller' : ''}`}>
                            {helpers.options.map(helper => <div key={`helper-${helper}`} className="helper" onClick={addHelper(helper)}>{helper}</div>)}
                        </div>
                    </div>
                : null }
            </div>
            {guide ? <div className="description">
                {guide}
            </div> : null}
            {note ? <div className="text-area-note">{note}</div> : null}
            {errors?.length ?
                <div className="textarea-errors">
                    {errors.map(error => <div className="error" key={`error-${error}`}>
                        <WarningIcon width={48} height={48} color='#ffa71c' />
                        <span>{error}</span>
                    </div>)}
                </div>
            : null}
        </div>
    );
}

export default TextAreaNext;